import { z } from "zod";

/**
 * Canonical entry-form validation schema. The form lives in several places
 * (legacy attorney UI, v1 v2-shim, the new v2 entry form); each local
 * `schema.ts` re-exports from here so the rules can't drift between copies.
 *
 * Rules mirror the v2 backend's loosened save-time contract
 * (`POST /v2/entries`, `PATCH /v2/entries/:id`):
 *
 *  Required:
 *    - date (must be parseable as a Date, not in the future)
 *    - duration (≥ 0; the action layer converts to `unitsSeconds`)
 *    - description (non-empty, not the "Please fill in the narrative." sentinel)
 *
 *  Optional:
 *    - matter_id / task / activity pass ""; action layer converts to null.
 *    - matter is display-only and may be null when the matter row has no number.
 *
 * Per-firm task/activity requirements have moved to the export pipeline on
 * the backend — do not re-add them here.
 */
export const timeEntrySchema = z.object({
  date: z
    .date({ message: "Date is required" })
    .refine(
      (d) => {
        const today = new Date();
        today.setHours(23, 59, 59, 999);
        return d <= today;
      },
      { message: "Date cannot be in the future" },
    ),
  matter_id: z.string(),
  matter: z.string().nullable(),
  task: z.string(),
  activity: z.string(),
  duration: z.number().nonnegative("Duration cannot be negative"),
  description: z
    .string()
    .refine((s) => s.trim().length > 0, "Narrative is required")
    .refine((s) => s.trim() !== "Please fill in the narrative.", {
      message: "Please write a real narrative",
    }),
});

export type TimeEntryFormValues = z.infer<typeof timeEntrySchema>;

export const defaultFormValues: TimeEntryFormValues = {
  date: new Date(),
  matter_id: "",
  matter: null,
  task: "",
  activity: "",
  duration: 0,
  description: "",
};
